import { propertyHooks } from '$src/lib/trpcHooks'
import { useLingui } from '@lingui/react/macro'
import { NumberInput, Select, Stack, Textarea, TextInput } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { AppModal } from '@markku/components'
import { propertyTypeEnum } from '@markku/db'
import { PROPERTY_TYPE_LABELS } from '@markku/utils'
import { useState } from 'react'

interface AddPropertyModalProps {
  opened: boolean
  onClose: () => void
  customerId?: number // Optional: if not provided, backend creates new customer
  onPropertyCreated?: (propertyId: number, propertyAddress: string) => void
}

export function AddPropertyModal({
  opened,
  onClose,
  customerId,
  onPropertyCreated,
}: AddPropertyModalProps) {
  const { t } = useLingui()
  const [street, setStreet] = useState('')
  const [city, setCity] = useState('')
  const [postalCode, setPostalCode] = useState('')
  const [propertyType, setPropertyType] = useState<string | null>(null)
  const [constructionYear, setConstructionYear] = useState<number | string>('')
  const [floorArea, setFloorArea] = useState<number | string>('')
  const [notes, setNotes] = useState('')

  const { mutateAsync: createProperty, isPending } = propertyHooks.useCreate()

  const handleSubmit = async () => {
    if (!street.trim() || !city.trim() || !propertyType) {
      notifications.show({
        title: t`Error`,
        message: t`Please fill in all required fields`,
        color: 'red',
      })
      return
    }

    const result = await createProperty({
      data: {
        // Use provided customerId, or 0 as placeholder (backend will auto-create)
        customerId: customerId ?? 0,
        propertyType: propertyType as
          | 'detached_house'
          | 'semi_detached'
          | 'row_house'
          | 'apartment_building',
        constructionYear: constructionYear ? Number(constructionYear) : null,
        notes: notes.trim() || null,
        data: {
          floorArea: floorArea ? Number(floorArea) : null,
        },
        address: {
          street: street.trim(),
          city: city.trim(),
          postalCode: postalCode.trim() || null,
          country: null,
          latitude: null,
          longitude: null,
        },
      },
    })

    const fullAddress = `${street.trim()}, ${city.trim()}`

    if (onPropertyCreated) {
      onPropertyCreated(result.id, fullAddress)
    } else {
      onClose()
    }
  }

  return (
    <AppModal
      opened={opened}
      onClose={onClose}
      title={t`Add property`}
      size="md"
      confirmProps={{
        label: t`Save`,
        onClick: handleSubmit,
        loading: isPending,
      }}
    >
      <Stack gap="md">
        <TextInput
          label={t`Street address`}
          placeholder={t`e.g. 123 Main Street`}
          value={street}
          onChange={(e) => setStreet(e.currentTarget.value)}
          autoComplete="street-address"
          required
        />
        <TextInput
          label={t`City`}
          placeholder={t`e.g. Helsinki`}
          value={city}
          onChange={(e) => setCity(e.currentTarget.value)}
          autoComplete="address-level2"
          required
        />
        <TextInput
          label={t`Postal code`}
          placeholder={t`e.g. 00100`}
          value={postalCode}
          onChange={(e) => setPostalCode(e.currentTarget.value)}
          autoComplete="postal-code"
        />
        <Select
          label={t`Property type`}
          placeholder={t`Select type`}
          value={propertyType}
          onChange={setPropertyType}
          data={propertyTypeEnum.enumValues.map((value) => ({
            value,
            label: PROPERTY_TYPE_LABELS[value],
          }))}
          required
        />
        <NumberInput
          label={t`Construction year`}
          placeholder={t`e.g. 2010`}
          value={constructionYear}
          onChange={setConstructionYear}
          min={1800}
          max={new Date().getFullYear() + 1}
        />
        <NumberInput
          label={t`Living area`}
          description={t`Floor area in square meters (m²)`}
          placeholder={t`e.g. 150`}
          value={floorArea}
          onChange={setFloorArea}
          min={0}
          max={500}
        />
        <Textarea
          label={t`Notes`}
          placeholder={t`Free-form notes about the property`}
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
          rows={3}
        />
      </Stack>
    </AppModal>
  )
}
